import React, { Fragment, useState, useRef, useEffect } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { usePopper } from "react-popper";
import { bottom, left, right, top } from "@popperjs/core";
import { classNames } from "../../helpers/classNames";
import Image from "./Image";
import Search from "../form/Search";
import Indentification from "./Indentification";
import { initialsValue } from "../../helpers";

const placements = {
  bottom: "bottom",
  "bottom-left": "bottom-start",
  "bottom-right": "bottom-end",
  top: "top",
  "top-left": "top-start",
  "top-right": "top-end",
  right: "right",
  left: "left",
  "": "bottom",
};

const getFallbackPlacements = (xPlacement) => {
  const fallbacks = {
    bottom: ["bottom-start", "bottom-end", "top", "top-start", "top-end"],
    "bottom-left": ["bottom-start", "bottom", "bottom-end", "top-start", "top"],
    "bottom-right": ["bottom-end", "bottom", "bottom-start", "top-end", "top"],
    top: ["top-start", "top-end", "bottom", "bottom-start", "bottom-end"],
    "top-left": ["top-start", "top", "top-end", "bottom-start", "bottom"],
    "top-right": ["top-end", "top", "top-start", "bottom-end", "bottom"],
    right: ["right", "left", "bottom-end", "top-end"],
    left: ["left", "right", "bottom-start", "top-start"],
    "": ["bottom-start", "bottom-end", "top", "top-start", "top-end"],
  };

  return fallbacks[xPlacement] || fallbacks.bottom;
};

const Switcher = ({
  dropdownData = [],
  selectedValue,
  onChange = () => {},
  placeholder,
  isSearch = false,
  xPlacement = "bottom-left",
  flip = true,
  dropdownClass,
  dropdownButtonClass,
  optionsClass,
  onSearchHandler = () => {},
  onScroll = () => {},
  loading,
  label,
  labelClasses,
  errorType,
  errorText,
  required,
  isDisabled = false,
  checked = true,
  offset = 8,
  strategy = "absolute",
  note,
  noteClasses,
  ...props
}) => {
  const [selected, setSelected] = useState(selectedValue || null);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const dropdownRef = useRef(null);

  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: placements[xPlacement],
      strategy: strategy,
      modifiers: [
        { name: "offset", options: { offset: [0, offset] } },
        { name: "preventOverflow", options: { padding: 8 } },
        { name: "computeStyles", options: { gpuAcceleration: false } },
        flip
          ? {
              name: "flip",
              options: {
                fallbackPlacements: getFallbackPlacements(xPlacement),
                flipVariations: true,
              },
            }
          : {
              name: "flip",
              enabled: false,
            },
      ].filter(Boolean),
    }
  );

  useEffect(() => {
    if (open && update) {
      update();
    }
  }, [open, update]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    setList(dropdownData);
  }, [JSON.stringify(dropdownData)]);

  useEffect(() => {
    if (selectedValue !== undefined && dropdownData?.length > 0) {
      setSelected(
        typeof selectedValue === "object"
          ? selectedValue
          : dropdownData.find(
              (item) =>
                item?.value === selectedValue || item?._id === selectedValue
            ) || null
      );
    } else {
      setSelected(null);
    }
  }, [JSON.stringify(selectedValue), JSON.stringify(dropdownData)]);

  return (
    <div className="relative w-full">
      <Listbox
        as={"div"}
        className="relative"
        value={selected}
        onChange={(val) => {
          setSelected(val);
          onChange(val?.value, val);
        }}
        disabled={isDisabled}
      >
        {({ open }) => (
          <>
            <ListboxButton
              ref={setReferenceElement}
              className="flex items-center w-full text-left"
            >
              <div
                className={classNames(
                  "relative w-full h-12 flex items-center gap-2 pr-2 pl-0 rounded-md bg-transparent text-xl font-semibold text-black !ring-0 !outline-0 transition-all duration-200 ",
                  dropdownButtonClass
                )}
              >
                {/* {selected?.image && (
                  <div className="w-5 aspect-square overflow-hidden rounded-md flex-shrink-0">
                    <Image src={selected?.image} alt={selected?.image} quality={0.2} classes={"w-full h-full object-cover object-center"} />
                  </div>
                )}
                {selected?.icon && (
                  <div className="w-5 aspect-1 overflow-hidden rounded-md flex-shrink-0">
                    <div className="w-5 aspect-1 overflow-hidden rounded-md flex-shrink-0">
                      <i className={classNames("fa-fw", selected?.icon)}></i>
                    </div>
                  </div>
                )} */}
                <div className="whitespace-nowrap flex-grow truncate">
                  {selected?.name || (
                    <span className="text-black">{placeholder}</span>
                  )}
                </div>
                <div className="text-base text-black ml-auto absolute top-1/2 right-2 -translate-y-1/2">
                  {loading ? (
                    <i
                      className={
                        "fa-duotone fa-spinner-third animate-spin text-sm"
                      }
                    />
                  ) : (
                    <i className={"fa-solid fa-fw fa-caret-down"}></i>
                  )}
                </div>
              </div>
            </ListboxButton>
            <Transition
              as={Fragment}
              className="z-20"
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              beforeEnter={() => setPopperElement(setPopperElement.current)}
              afterLeave={() => {
                setPopperElement(null);
              }}
              ref={setPopperElement}
              style={styles.popper}
              {...attributes.popper}
            >
              <ListboxOptions
                modal={false}
                //anchor="bottom"
                //transition
                className={classNames(
                  "mt-1 w-72 origin-top-right bg-white divide-y divide-gray-200 rounded-md shadow-lg border border-gray-200 ring-0 !overflow-hidden",
                  dropdownClass
                )}
              >
                {isSearch && (
                  <div className="py-2 px-3">
                    <Search
                      placeholder={"Search"}
                      iconClasses={""}
                      search={(e) => onSearchHandler(e)}
                    />
                  </div>
                )}
                {Array.isArray(list) && !loading && list?.length > 0 && (
                  <div
                    className="overflow-auto mb-1 w-full py-1 px-1 flex flex-col max-h-60 scrollbar space-y-1"
                    onScroll={onScroll}
                  >
                    {list.map((item, index) => (
                      <ListboxOption
                        key={index}
                        onMouseOver={(e) => {}}
                        className={() =>
                          classNames(
                            "relative py-3 pl-3 pr-10 text-sm cursor-pointer transition-all duration-200 text-gray-800 bg-white hover:bg-primary rounded-lg group hover:text-white",
                            optionsClass
                          )
                        }
                        value={item}
                      >
                        <>
                          <div
                            className={classNames(
                              "truncate flex items-center gap-2"
                            )}
                          >
                            {item?.icon && (
                              <div className="w-5 aspect-square overflow-hidden rounded-md flex-shrink-0">
                                <i
                                  className={classNames("fa-fw", item?.icon)}
                                ></i>
                              </div>
                            )}
                            {/* {item?.image && ( */}
                            <div className="w-8 aspect-square overflow-hidden rounded-full flex-shrink-0">
                              {/* <Image
                                src={item.image}
                                alt={item.image}
                                classes={
                                  "w-full h-full object-cover object-center"
                                }
                              /> */}
                              <Indentification
                                image={item.image}
                                alt={item.image || item?.name}
                                initial={initialsValue(item?.name)}
                                className={"overflow-hidden"}
                                size="sm"
                              />
                            </div>
                            {/* // )} */}
                            <div className="flex flex-col font-semibold">
                              {item?.name}
                              {item?.description && (
                                <div
                                  className={classNames(
                                    "text-xs font-normal whitespace-pre-wrap text-gray-500 group-hover:text-white/50 transition-all duration-200"
                                  )}
                                >
                                  {item?.description}
                                </div>
                              )}
                            </div>
                          </div>
                          {selected?.value === item?.value && checked && (
                            <div className="absolute inset-y-0 right-4 flex items-center pl-2 text-base">
                              <i className="fa-regular fa-fw fa-check"></i>
                            </div>
                          )}
                        </>
                      </ListboxOption>
                    ))}
                  </div>
                )}
              </ListboxOptions>
            </Transition>
          </>
        )}
      </Listbox>
    </div>
  );
};

export default Switcher;
